import type {
  ComplianceOutreachDto,
  DailyMedDay,
  DayStatus,
  DailyMedicationDto,
  DailyMedicationRow,
  DailyStreamDay,
  DailyStreamDto,
  DailySummaryDto,
  DailySummaryErrorCode,
  OutreachEvent,
  PatientSidebar
} from "./complianceTypes";
import type { MedBasis, MissingDataStream } from "./complianceFilters";
import { isValidTimeZone } from "./complianceClock";
import { inMedBasis, isPdTag, medicationName } from "./complianceMeds";
import type { MedicationOption } from "../categoryMetadata";
import { noScheduleReason } from "./complianceFilters";

// The response always has these four stream keys (an empty list when the patient has no such
// stream or it failed); bowel movements are their own section.
const STREAM_KEY_MAP: Record<string, MissingDataStream> = {
  mm4pd: "tremor_dyskinesia",
  steps: "mobility",
  sleep: "sleep",
  tap_test: "tap_test"
};

// Errors are keyed by stream name or by section name.
const ERROR_KEY_MAP: Record<string, MissingDataStream> = {
  ...STREAM_KEY_MAP,
  medication: "medication",
  bowel_movements: "bowel_movement"
};

export function sectionErrorText(
  code: DailySummaryErrorCode,
  label: string
): string {
  switch (code) {
    case "upstream_timeout":
      return `Couldn't load ${label}: the request timed out`;
    case "multiple_streams":
      // The endpoint won't guess which device's data to show.
      return `Couldn't load ${label}: more than one matching data stream`;
    default:
      // upstream_error, and codes added to the endpoint later read as a generic failure.
      return `Couldn't load ${label}`;
  }
}

export function parseDailyStream(dtos: DailyStreamDto[]): DailyStreamDay[] {
  return dtos.map((d) => ({
    date: d.date,
    present: d.present,
    value: d.value
  }));
}

export function parseDailyMedication(
  dto: DailyMedicationDto
): DailyMedicationRow {
  return {
    date: dto.date,
    itemId: dto.item_id,
    runeClassification: dto.rune_classification,
    pdTag: dto.pd_tag,
    scheduled: dto.scheduled,
    taken: dto.taken,
    onTime: dto.on_time,
    skipped: dto.skipped,
    scheduledAutolog: dto.scheduled_autolog,
    autologLogged: dto.autolog_logged,
    lastTakenAt: dto.last_taken_at,
    lastAutologAt: dto.last_autolog_at
  };
}

// The drawer does date arithmetic on these, which throws on an invalid date. Round-tripped
// because Date.parse rolls impossible days over (2026-02-30 is March 2).
function isIsoDate(date: unknown): date is string {
  if (typeof date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return false;
  }
  const ms = Date.parse(date + "T00:00:00Z");
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === date;
}

export function parseDailySummary(dto: DailySummaryDto): PatientSidebar {
  if (!isIsoDate(dto.start_date) || !isIsoDate(dto.end_date)) {
    throw new Error(
      `Daily summary has an invalid date range: ${dto.start_date} to ${dto.end_date}`
    );
  }
  const tz =
    dto.timezone_missing || !isValidTimeZone(dto.timezone)
      ? "UTC"
      : dto.timezone;
  const timezoneMissing =
    dto.timezone_missing || !isValidTimeZone(dto.timezone);

  const streams: Partial<Record<MissingDataStream, DailyStreamDay[]>> = {};
  for (const [contractKey, ourKey] of Object.entries(STREAM_KEY_MAP)) {
    const dtos = (dto.streams as Record<string, DailyStreamDto[]>)[contractKey];
    if (dtos) {
      streams[ourKey] = parseDailyStream(dtos);
    }
  }

  if (dto.bowel_movements) {
    streams.bowel_movement = dto.bowel_movements.map((bm) => ({
      date: bm.date,
      present: bm.present,
      value: null
    }));
  }

  const errors: PatientSidebar["errors"] = {};
  for (const [contractKey, code] of Object.entries(dto.errors)) {
    const ourKey = ERROR_KEY_MAP[contractKey];
    if (ourKey) errors[ourKey] = code;
  }

  return {
    timezone: tz,
    timezoneMissing,
    startDate: dto.start_date,
    endDate: dto.end_date,
    streams,
    medicationDays: dto.medication.days.map(parseDailyMedication),
    errors,
    outreach: []
  };
}

export interface MedicationSeries {
  itemId: number;
  runeClassification: string;
  days: DailyMedDay[];
  autologOnly: boolean;
}

function dayStatus(
  date: string,
  today: string,
  scheduled: number,
  scheduledAutolog: number
): DayStatus {
  if (date === today) return "in_progress";
  if (scheduled > 0) return "scheduled";
  return scheduledAutolog > 0 ? "autolog_only" : "no_schedule";
}

export function accountedPct(
  days: DailyMedDay[]
): { pct: number; accounted: number; scheduled: number } | null {
  const scheduledDays = days.filter((d) => d.status === "scheduled");
  if (scheduledDays.length === 0) return null;
  const totalScheduled = scheduledDays.reduce((sum, d) => sum + d.scheduled, 0);
  const totalAccounted = scheduledDays.reduce((sum, d) => sum + d.accounted, 0);
  const pct =
    totalScheduled > 0
      ? Math.round((totalAccounted / totalScheduled) * 100)
      : 0;
  return { pct, accounted: totalAccounted, scheduled: totalScheduled };
}

export function sumMedicationSeries(
  series: MedicationSeries[],
  dates: string[],
  today: string
): DailyMedDay[] {
  const dailyTotals: Record<string, DailyMedDay> = {};
  for (const date of dates) {
    dailyTotals[date] = {
      date,
      scheduled: 0,
      taken: 0,
      skipped: 0,
      accounted: 0,
      scheduledAutolog: 0,
      autologLogged: 0,
      status: "no_schedule"
    };
  }

  for (const s of series) {
    for (const day of s.days) {
      const total = dailyTotals[day.date];
      if (total) {
        total.scheduled += day.scheduled;
        total.taken += day.taken;
        total.skipped += day.skipped;
        total.accounted += day.accounted;
        total.scheduledAutolog += day.scheduledAutolog;
        total.autologLogged += day.autologLogged;
      }
    }
  }

  return dates.map((date) => {
    const day = dailyTotals[date];
    day.status = dayStatus(date, today, day.scheduled, day.scheduledAutolog);
    return day;
  });
}

export function medicationSeriesForBasis(
  rows: DailyMedicationRow[],
  basis: MedBasis,
  studyMedications: string[],
  dates: string[],
  today: string
): MedicationSeries[] {
  const studySet = new Set(studyMedications);

  const itemGroups = new Map<number, DailyMedicationRow[]>();
  for (const row of rows) {
    if (
      !inMedBasis(
        {
          runeClassification: row.runeClassification,
          isPd: isPdTag(row.pdTag)
        },
        basis,
        studySet
      )
    ) {
      continue;
    }
    const existing = itemGroups.get(row.itemId);
    if (existing) {
      existing.push(row);
    } else {
      itemGroups.set(row.itemId, [row]);
    }
  }

  const series: MedicationSeries[] = [];

  for (const [itemId, itemRows] of Array.from(itemGroups.entries())) {
    const rowsByDate = new Map(itemRows.map((r) => [r.date, r]));
    const days: DailyMedDay[] = dates.map((date) => {
      const row = rowsByDate.get(date);
      if (!row) {
        return {
          date,
          scheduled: 0,
          taken: 0,
          skipped: 0,
          accounted: 0,
          scheduledAutolog: 0,
          autologLogged: 0,
          status: dayStatus(date, today, 0, 0)
        };
      }

      const accounted = Math.min(row.scheduled, row.taken + row.skipped);
      const status = dayStatus(
        date,
        today,
        row.scheduled,
        row.scheduledAutolog
      );

      return {
        date,
        scheduled: row.scheduled,
        taken: row.taken,
        skipped: row.skipped,
        accounted,
        scheduledAutolog: row.scheduledAutolog,
        autologLogged: row.autologLogged,
        status
      };
    });

    const hasScheduled = days.some((d) => d.status === "scheduled");
    const autologOnly =
      !hasScheduled && days.some((d) => d.status === "autolog_only");

    if (hasScheduled) {
      const runeClassification = itemRows[0].runeClassification;
      series.push({ itemId, runeClassification, days, autologOnly: false });
    } else if (autologOnly) {
      const runeClassification = itemRows[0].runeClassification;
      series.push({ itemId, runeClassification, days, autologOnly: true });
    }
  }

  return series;
}

export function parseIntercomOutreach(
  dtos: ComplianceOutreachDto[]
): OutreachEvent[] {
  const events = dtos.map((d) => ({
    source: "intercom" as const,
    occurredAt: new Date(d.contactedAt * 1000).toISOString(),
    // complianceOutreachList has no channel; compliance outreach goes out by email.
    channel: "email",
    externalId: d.messageId
  }));
  events.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  return events;
}

const BASIS_DOSE_LABELS: Record<MedBasis, string> = {
  all: "all medication",
  study: "study medication",
  pd: "PD medication"
};

export const AUTOLOG_ONLY_TEXT = "All scheduled doses are auto-logged";

export function adherenceSummary(
  days: DailyMedDay[],
  basis: MedBasis,
  windowLength: number
): string {
  const result = accountedPct(days);
  if (!result) {
    return days.some((d) => d.status === "autolog_only")
      ? AUTOLOG_ONLY_TEXT
      : noScheduleReason(basis);
  }

  const completedDays = windowLength - 1;
  return `${result.pct}% of ${BASIS_DOSE_LABELS[basis]} doses accounted for over past ${completedDays}d`;
}

// Study drugs first, then by the name users see (not the classification slug).
export function namedSeriesForDisplay(
  series: MedicationSeries[],
  studyMedications: string[],
  catalog: MedicationOption[]
): Array<{ series: MedicationSeries; name: string }> {
  const studySet = new Set(studyMedications);
  return series
    .map((s) => ({
      series: s,
      name: medicationName(s.runeClassification, catalog)
    }))
    .sort(
      (a, b) =>
        Number(studySet.has(b.series.runeClassification)) -
          Number(studySet.has(a.series.runeClassification)) ||
        a.name.localeCompare(b.name)
    );
}
